// OpenAI API for job data extraction

// Helper function to extract company website
const extractCompanyWebsite = (jobText, jobUrl) => {
  // Try to extract from job text first
  const websitePatterns = [
    /(?:website|site|learn more|visit)[:\s]+(https?:\/\/[^\s]+)/i,
    /(?:company|corporate)[:\s]+(https?:\/\/[^\s]+)/i,
    /https?:\/\/[^\s]+(?:\.com|\.org|\.net|\.io)(?:\/[^\s]*)?/i,
  ];

  for (const pattern of websitePatterns) {
    const match = jobText.match(pattern);
    if (match && match[1]) {
      const website = match[1].trim();
      // Clean up the URL
      if (website.includes(".")) {
        return website.startsWith("http") ? website : `https://${website}`;
      }
    }
  }

  // Try to extract from job URL domain
  if (jobUrl) {
    try {
      const urlObj = new URL(jobUrl);
      const domain = urlObj.hostname;

      // Skip job board domains
      const jobBoards = [
        "linkedin.com",
        "indeed.com",
        "glassdoor.com",
        "monster.com",
        "careerbuilder.com",
      ];
      if (!jobBoards.some((board) => domain.includes(board))) {
        // Extract company name from path and create website
        const pathParts = urlObj.pathname
          .split("/")
          .filter((part) => part.length > 0);
        const possibleCompany = pathParts[0] || domain.split(".")[0];

        if (possibleCompany && possibleCompany.length > 2) {
          return `https://www.${possibleCompany
            .replace(/[^a-zA-Z0-9]/g, "")
            .toLowerCase()}.com`;
        }
      }
    } catch (e) {
      console.log("Error parsing job URL for website extraction:", e);
    }
  }

  return null;
};

// Fallback extraction when API fails due to CORS/network issues
const fallbackExtraction = (jobText, jobUrl = null) => {
  console.log("=== Fallback Extraction Debug ===");
  console.log("Input text length:", jobText.length);
  console.log("Input text preview:", jobText.substring(0, 300) + "...");
  console.log("Job URL:", jobUrl);

  const extractFromText = (text) => {
    const companyPatterns = [
      /(?:at|@)\s+([A-Z][a-zA-Z0-9\s&.,-]+?)(?:\s+(?:is hiring|we are|are looking for))/i,
      /([A-Z][a-zA-Z0-9\s&.,-]+?)(?:\s+(?:is hiring|we are|are looking for))/i,
    ];

    const titlePatterns = [
      /(?:job title|position|role)[:\s]+([A-Z][a-zA-Z0-9\s\-/.,#()]+?)(?:\n|$)/i,
      /(?:hiring|looking for|seeking)[:\s]+([a-z][a-zA-Z0-9\s\-/.,#()]+?)(?:\n|\.|$)/i,
    ];

    const locationPatterns = [
      /location[:\s]+([A-Z][a-zA-Z\s,.-]+?)(?:\n|$)/i,
      /(Remote|Hybrid|On-site)/i,
    ];

    const commonSkills = [
      "JavaScript",
      "Python",
      "Java",
      "React",
      "Angular",
      "Vue",
      "Node.js",
      "TypeScript",
      "AWS",
      "Azure",
      "GCP",
      "Docker",
      "Kubernetes",
      "MongoDB",
      "PostgreSQL",
      "MySQL",
      "Redis",
      "Git",
      "Jira",
      "Agile",
      "Scrum",
      "CI/CD",
    ];

    let companyName = "Unknown Company";
    let jobTitle = "Unknown Position";
    let location = "Not specified";
    let skills = [];
    let salaryRange = null;

    console.log("Starting pattern matching...");

    // Extract company
    for (const pattern of companyPatterns) {
      const match = text.match(pattern);
      if (match && match[1] && match[1].trim().length > 2) {
        companyName = match[1].trim();
        console.log("Found company:", companyName);
        break;
      }
    }

    // Extract job title
    for (const pattern of titlePatterns) {
      const match = text.match(pattern);
      if (match && match[1] && match[1].trim().length > 2) {
        jobTitle = match[1].trim();
        console.log("Found job title:", jobTitle);
        break;
      }
    }

    // Extract location
    for (const pattern of locationPatterns) {
      const match = text.match(pattern);
      if (match) {
        location = match[1] || match[0];
        console.log("Found location:", location);
        break;
      }
    }

    // Extract salary
    const salaryMatch = text.match(/\$[\d,]+(?:\s*[-–]\s*\$[\d,]+|[kK])/);
    if (salaryMatch) {
      salaryRange = salaryMatch[0];
      console.log("Found salary:", salaryRange);
    }

    // Extract skills
    commonSkills.forEach((skill) => {
      if (text.toLowerCase().includes(skill.toLowerCase())) {
        skills.push(skill);
      }
    });
    console.log("Found skills:", skills);

    // Determine work type
    let workType = "On-site";
    if (text.toLowerCase().includes("remote")) workType = "Remote";
    else if (text.toLowerCase().includes("hybrid")) workType = "Hybrid";
    console.log("Determined work type:", workType);

    // Determine experience level
    let experienceLevel = "Not specified";
    if (
      text.toLowerCase().includes("senior") ||
      text.toLowerCase().includes("5+")
    ) {
      experienceLevel = "Senior-level";
    } else if (
      text.toLowerCase().includes("junior") ||
      text.toLowerCase().includes("entry")
    ) {
      experienceLevel = "Entry-level";
    } else if (
      text.toLowerCase().includes("mid") ||
      text.toLowerCase().includes("3-5")
    ) {
      experienceLevel = "Mid-level";
    }
    console.log("Determined experience level:", experienceLevel);

    // Extract company website
    const companyWebsite = extractCompanyWebsite(jobText, jobUrl);
    console.log("Extracted company website:", companyWebsite);

    // Create job description (first 500 chars)
    let jobDescription = text.replace(/\n+/g, " ").trim();
    if (jobDescription.length > 500) {
      jobDescription = jobDescription.substring(0, 497) + "...";
    }
    console.log("Created job description length:", jobDescription.length);

    const result = {
      companyName,
      jobTitle,
      location,
      workType,
      employmentType: "Full-time",
      salaryRange: salaryRange || null,
      experienceLevel,
      skillsRequired: skills.slice(0, 8),
      jobDescription,
      postedDate: new Date().toISOString().split("T")[0],
      applicationDeadline: "",
      jobUrl: jobUrl,
      companyWebsite: extractCompanyWebsite(jobText, jobUrl),
    };

    console.log("Fallback extraction result:", result);
    console.log("=== Fallback Extraction Complete ===");

    return result;
  };

  return extractFromText(jobText);
};

const openAIRequest = async (prompt) => {
  try {
    console.log("=== OpenAI API Request Debug ===");
    console.log("Prompt length:", prompt.length);
    console.log("Prompt preview:", prompt.substring(0, 300) + "...");

    const requestBody = {
      model: "gpt-4o-mini",
      messages: [
        {
          role: "user",
          content: prompt,
        },
      ],
      max_tokens: 4096,
      temperature: 0.1,
    };

    console.log("Request body:", JSON.stringify(requestBody, null, 2));

    const response = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization:
          "Bearer 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",
      },
      body: JSON.stringify(requestBody),
    });

    console.log("OpenAI API response status:", response.status);
    console.log(
      "OpenAI API response headers:",
      Object.fromEntries(response.headers.entries()),
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error("OpenAI API error details:", errorData);

      // Handle rate limit specifically
      if (response.status === 429) {
        console.log("Rate limit hit, using fallback extraction");
        throw new Error("Rate limit exceeded - using fallback extraction");
      }

      throw new Error(
        `OpenAI API error: ${response.status} - ${errorData.error?.message || "Unknown error"}`,
      );
    }

    const openAIData = await response.json();
    console.log(
      "OpenAI API response data:",
      JSON.stringify(openAIData, null, 2),
    );

    if (
      !openAIData.choices ||
      !openAIData.choices[0] ||
      !openAIData.choices[0].message
    ) {
      console.error("Invalid OpenAI response structure:", openAIData);
      throw new Error("Invalid response from OpenAI API");
    }

    const result = openAIData.choices[0].message.content;
    console.log("OpenAI response text length:", result.length);
    console.log("OpenAI response preview:", result.substring(0, 500) + "...");
    console.log("=== End OpenAI API Debug ===");

    return result;
  } catch (error) {
    console.error("OpenAI API call failed:", error);
    console.log("Error details:", {
      message: error.message,
      stack: error.stack,
    });
    throw error;
  }
};

// OpenAI API for job data extraction
const extractJobData = async (url) => {
  let htmlContent;

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    htmlContent = await response.text();
  } catch (fetchError) {
    console.log("Cannot fetch job page directly. Using URL-based extraction.");

    // When CORS blocks, provide domain-specific error messages
    if (url.includes("linkedin.com")) {
      throw new Error(
        "LinkedIn blocks automatic extraction. Please copy the job details and add manually.",
      );
    } else if (url.includes("indeed.com")) {
      throw new Error(
        "Indeed blocks automatic extraction. Please copy the job details and add manually.",
      );
    } else if (url.includes("adp.com")) {
      throw new Error(
        "ADP Jobs blocks automatic extraction. Please copy the job details and add manually.",
      );
    } else {
      throw new Error(
        "This job board blocks automatic extraction. Please add the job manually.",
      );
    }
  }

  // Use Puter's Claude 3.5 Sonnet for job data extraction
  try {
    const prompt = `You are an expert job posting analyzer. Extract structured data from this job posting with maximum accuracy.

YOUR ONLY TASK: Return a valid JSON object. NO other text, NO markdown, NO backticks, NO explanations.

START YOUR RESPONSE WITH: {
END YOUR RESPONSE WITH: }

EXTRACTION RULES BY FIELD:

companyName:
- Extract the EXACT company name as shown
- Check: meta tags (og:site_name), <title>, company logos, "About [Company]" sections
- Remove: "Inc", "LLC", "Ltd" ONLY if obviously added (keep if part of brand)
- If not found: extract from domain name

jobTitle:
- Extract EXACT title, preserve capitalization
- NEVER include company name
- Remove: "Apply", "Hot", "New", location tags
- Keep: levels, numbers (e.g., "Senior Engineer III")

location:
- Format: "City, State" for US, "City, Country" for international
- Remote: return exactly "Remote"
- Hybrid: "City, State (Hybrid)"
- Multiple locations: "City1, State1; City2, State2"

workType:
- MUST be EXACTLY one of: "Remote", "Hybrid", "On-site"
- Remote: "100% remote", "fully remote", "work from anywhere"
- Hybrid: "hybrid", "flexible", "2-3 days office"
- Default: "On-site" if unclear

employmentType:
- MUST be EXACTLY one of: "Full-time", "Part-time", "Contract", "Internship", "Temporary"
- Full-time: "FTE", "permanent", "40 hours"
- Contract: "contractor", "freelance", "6 month"
- Default: "Full-time" if not specified

salaryRange:
- Extract EXACT text with currency: "$80,000-$120,000", "€50k-70k"
- Include equity: "$120k + equity"
- If "Competitive", "DOE": use exact phrase
- If not mentioned: null

experienceLevel:
- MUST be EXACTLY one of: "Entry-level", "Mid-level", "Senior-level", "Executive", "Internship", "Not specified"
- Entry: "0-2 years", "junior", "graduate"
- Mid: "3-5 years", "intermediate"
- Senior: "5+ years", "senior", "lead", "staff", "principal"
- Executive: "director", "VP", "C-level"

skillsRequired:
- Extract EVERY skill mentioned ANYWHERE in the posting
- Categories to find:
  * Programming: Python, Java, JavaScript, Go, Ruby, C++, Rust
  * Frameworks: React, Angular, Vue, Django, Flask, Spring, .NET
  * Cloud: AWS, Azure, GCP, Docker, Kubernetes, Terraform
  * Databases: PostgreSQL, MongoDB, MySQL, Redis, Cassandra
  * Tools: Git, Jenkins, Jira, Tableau, Salesforce, Excel
  * Methods: Agile, Scrum, CI/CD, TDD, DevOps, Microservices
  * Soft skills: Leadership, Communication, Problem-solving, Teamwork
  * Domain: Machine Learning, Data Science, Finance, Healthcare
- Look in: Requirements, Qualifications, Responsibilities, Nice-to-have sections
- Format: ["Python", "React", "AWS", "Leadership", "5+ years experience"]
- Return empty array [] only if truly no skills found

jobDescription:
- Create 400-600 character comprehensive summary
- Include: Main responsibilities (2-3 key duties), must-have qualifications (top 3-5), team/product context
- Structure: "As [role], you will [main duty]. Responsibilities include [items]. Requirements: [qualifications]. [Context if relevant]."
- Be specific, avoid generic phrases like "exciting opportunity"
- Example: "As a Senior Data Engineer, you will design data pipelines processing 100M+ events daily. Responsibilities include building ETL workflows, optimizing database performance, mentoring junior engineers. Requirements: 5+ years Python/SQL, Airflow and AWS experience, strong distributed systems knowledge. Join our 50-person data team."

postedDate:
- Convert to YYYY-MM-DD format (ISO 8601)
- Look for: "Posted on", "Date posted", "Published", <time> tags
- Relative dates: "Posted 2 days ago" → calculate from today, "Today" → current date
- Date format conversions:
  * "January 15, 2025" → "2025-01-15"
  * "01/15/2025" → "2025-01-15"
  * "15 Jan 2025" → "2025-01-15"
- Default: current date if not found

companyWebsite:
- Extract EXACT company website URL
- Check: meta tags, company logos with links, "About [Company]" sections, footer links
- Look for: "Visit our website", "Learn more at", company social media links
- Extract from domain: if no explicit website, use clean company domain (e.g., "https://company.com")
- Remove: job board URLs, tracking links, affiliate links
- Format: "https://www.company.com" or null if not found
- If multiple websites: choose the main corporate site

applicationDeadline:
- Convert to YYYY-MM-DD format
- Look for: "Apply by", "Deadline", "Applications close", "Closing date"
- If "Rolling basis", "Until filled", "No deadline": return ""
- Default: "" if not mentioned

PARSING STRATEGY:
1. Scan ENTIRE HTML content thoroughly
2. Check structured data: JSON-LD, meta tags, microdata, data attributes
3. Look for common class names: job-title, company-name, location, salary, requirements, skills
4. Parse all bullet points and lists carefully
5. Check section headings (h1-h6) for markers like "Requirements:", "Qualifications:", "About the Role:"

QUALITY CHECKS:
- If companyName is generic ("Company", "Employer"), search harder in the HTML
- If jobTitle contains company name, remove it
- If skillsRequired has fewer than 3 items, scan more carefully - most jobs list many skills
- If jobDescription is under 300 characters, it's incomplete - expand it
- Verify all dates are in YYYY-MM-DD format

CRITICAL: Return ONLY this JSON structure (no text before or after):
{
  "companyName": "string",
  "jobTitle": "string",
  "location": "string",
  "workType": "Remote|Hybrid|On-site",
  "employmentType": "Full-time|Part-time|Contract|Internship|Temporary",
  "salaryRange": "string or null",
  "experienceLevel": "Entry-level|Mid-level|Senior-level|Executive|Internship|Not specified",
  "skillsRequired": ["skill1", "skill2", "skill3"],
  "jobDescription": "string (400-600 chars)",
  "postedDate": "YYYY-MM-DD",
  "applicationDeadline": "",
  "companyWebsite": "string or null"
}

Job URL: ${url}

HTML Content to analyze:
${htmlContent.substring(0, 40000)}`;

    // Call OpenAI API
    const response = await openAIRequest(prompt);

    console.log("OpenAI response:", response);

    console.log("=== JSON Parsing Debug ===");
    console.log("Response type:", typeof response);
    console.log("Response length:", response.length);
    console.log("Response preview:", response.substring(0, 1000) + "...");

    // Parse the response
    let responseText = response;
    if (typeof response === "object" && response.message) {
      responseText = response.message;
      console.log("Extracted message from object response");
    }

    console.log("Response text type:", typeof responseText);
    console.log("Response text preview:", responseText.substring(0, 500));

    // Remove markdown code blocks if present
    let jsonText = responseText.trim();
    console.log("Original trimmed text:", jsonText.substring(0, 200));

    jsonText = jsonText.replace(/```json\s*/g, "").replace(/```\s*/g, "");
    console.log("After removing markdown:", jsonText.substring(0, 200));

    // Extract JSON object
    const jsonMatch = jsonText.match(/\{[\s\S]*\}/);
    console.log(
      "JSON match result:",
      jsonMatch ? "Found JSON" : "No JSON found",
    );

    if (!jsonMatch) {
      console.error("No JSON found in response. Full response:", responseText);
      console.log("=== JSON Parsing Failed ===");
      throw new Error("No valid JSON found in OpenAI response");
    }

    console.log("Found JSON match:", jsonMatch[0].substring(0, 300) + "...");
    console.log("JSON length:", jsonMatch[0].length);

    const jobData = JSON.parse(jsonMatch[0]);
    console.log("Successfully parsed JSON:", jobData);
    console.log("Parsed job data keys:", Object.keys(jobData));
    console.log("=== JSON Parsing Success ===");

    // Validate required fields
    if (!jobData.companyName || !jobData.jobTitle) {
      throw new Error("Missing required fields in extracted data");
    }

    return {
      ...jobData,
      jobUrl: url,
    };
  } catch (error) {
    console.error("Error extracting job data:", error);

    // Check if this is a CORS/domain-specific error
    if (error.message.includes("blocks automatic extraction")) {
      throw new Error(
        `${error.message} Please paste the job description manually for extraction.`,
      );
    }

    // Check if this is an OpenAI API error
    if (
      error.message.includes("AI extraction failed") ||
      error.message.includes("Failed to parse job data") ||
      error.message.includes("Failed to fetch") ||
      error.message.includes("Rate limit exceeded")
    ) {
      console.log("=== Using Fallback for URL Extraction ===");
      console.log("Reason for fallback:", error.message);

      // Use fallback extraction with HTML content
      const fallbackData = fallbackExtraction(htmlContent, url);
      console.log("Fallback extracted data for URL:", fallbackData);
      console.log("=== URL Fallback Complete ===");

      return fallbackData;
    }

    // General fallback: allow manual job description input
    throw new Error(
      `Failed to extract from URL: ${error.message}. Please paste the job description manually for extraction.`,
      {
        requiresManualInput: true,
        originalError: error.message,
        url: url,
      },
    );
  }
};

// Simple function to extract job data from pasted job description (no URL required)
const extractFromPastedJob = async (jobDescriptionText, jobUrl = null) => {
  console.log("=== Extract From Pasted Job Start ===");
  console.log("Input text length:", jobDescriptionText.length);
  console.log("Input preview:", jobDescriptionText.substring(0, 200) + "...");
  console.log("Job URL:", jobUrl);

  try {
    const result = await extractJobDataFromText(jobDescriptionText, jobUrl);
    console.log("=== Extract From Pasted Job Success ===");
    return result;
  } catch (error) {
    console.error("=== Extract From Pasted Job Failed ===");
    console.error("Error:", error);
    throw error;
  }
};

// Extract job data from manually pasted job description
const extractJobDataFromText = async (jobDescriptionText, jobUrl) => {
  try {
    const prompt = `You are an expert job posting analyzer. Extract structured data from this job posting with maximum accuracy.

YOUR ONLY TASK: Return a valid JSON object. NO other text, NO markdown, NO backticks, NO explanations.
START YOUR RESPONSE WITH: {
END YOUR RESPONSE WITH: }

EXTRACTION RULES BY FIELD:

companyName:
- Extract the EXACT company name as shown
- Look for "at [Company]", "[Company] is hiring", company mentions
- Remove: "Inc", "LLC", "Ltd" ONLY if obviously added (keep if part of brand)
- If not found: extract from jobUrl domain or use "Not specified"

jobTitle:
- Extract EXACT title, preserve capitalization
- NEVER include company name
- Remove: "Apply", "Hot", "New", location tags
- Keep: levels, numbers (e.g., "Senior Engineer III")

location:
- Format: "City, State" for US, "City, Country" for international
- Remote: return exactly "Remote"
- Hybrid: "City, State (Hybrid)"
- Multiple locations: "City1, State1; City2, State2"

workType:
- MUST be EXACTLY one of: "Remote", "Hybrid", "On-site"
- Remote: "100% remote", "fully remote", "work from anywhere"
- Hybrid: "hybrid", "flexible", "2-3 days office"
- Default: "On-site" if unclear

employmentType:
- MUST be EXACTLY one of: "Full-time", "Part-time", "Contract", "Internship", "Temporary"
- Full-time: "FTE", "permanent", "40 hours"
- Contract: "contractor", "freelance", "6 month"
- Default: "Full-time" if not specified

salaryRange:
- Extract EXACT text with currency: "$80,000-$120,000", "€50k-70k"
- Include equity: "$120k + equity"
- If "Competitive", "DOE": use exact phrase
- If not mentioned: null

experienceLevel:
- MUST be EXACTLY one of: "Entry-level", "Mid-level", "Senior-level", "Executive", "Internship", "Not specified"
- Entry: "0-2 years", "junior", "graduate"
- Mid: "3-5 years", "intermediate"
- Senior: "5+ years", "senior", "lead", "staff", "principal"
- Executive: "director", "VP", "C-level"

skillsRequired:
- Extract EVERY skill mentioned ANYWHERE in the posting
- Categories to find:
  * Programming: Python, Java, JavaScript, Go, Ruby, C++, Rust
  * Frameworks: React, Angular, Vue, Django, Flask, Spring, .NET
  * Cloud: AWS, Azure, GCP, Docker, Kubernetes, Terraform
  * Databases: PostgreSQL, MongoDB, MySQL, Redis, Cassandra
  * Tools: Git, Jenkins, Jira, Tableau, Salesforce, Excel
  * Methods: Agile, Scrum, CI/CD, TDD, DevOps, Microservices
  * Soft skills: Leadership, Communication, Problem-solving, Teamwork
  * Domain: Machine Learning, Data Science, Finance, Healthcare
- Look in: Requirements, Qualifications, Responsibilities, Nice-to-have sections
- Format: ["Python", "React", "AWS", "Leadership", "5+ years experience"]
- Return empty array [] only if truly no skills found

jobDescription:
- Create 400-600 character comprehensive summary
- Include: Main responsibilities (2-3 key duties), must-have qualifications (top 3-5), team/product context
- Structure: "As [role], you will [main duty]. Responsibilities include [items]. Requirements: [qualifications]. [Context if relevant]."
- Be specific, avoid generic phrases like "exciting opportunity"

postedDate:
- Convert to YYYY-MM-DD format (ISO 8601)
- Look for: "Posted on", "Date posted", "Published", <time> tags
- Relative dates: "Posted 2 days ago" → calculate from today, "Today" → current date
- Default: current date if not found

applicationDeadline:
- Convert to YYYY-MM-DD format
- Look for: "Apply by", "Deadline", "Applications close", "Closing date"
- If "Rolling basis", "Until filled", "No deadline": return ""
- Default: "" if not mentioned

CRITICAL: Return ONLY this JSON structure (no text before or after):
{
  "companyName": "string",
  "jobTitle": "string",
  "location": "string",
  "workType": "Remote|Hybrid|On-site",
  "employmentType": "Full-time|Part-time|Contract|Internship|Temporary",
  "salaryRange": "string or null",
  "experienceLevel": "Entry-level|Mid-level|Senior-level|Executive|Internship|Not specified",
  "skillsRequired": ["skill1", "skill2", "skill3"],
  "jobDescription": "string (400-600 chars)",
  "postedDate": "YYYY-MM-DD",
  "applicationDeadline": ""
}

Job URL: ${jobUrl || "Not provided"}

Job Description Text:
${jobDescriptionText.substring(0, 40000)}`;

    // Call OpenAI API
    const response = await openAIRequest(prompt);

    console.log("OpenAI response from manual text:", response);

    console.log("=== Manual Text JSON Parsing Debug ===");
    console.log("Manual response type:", typeof response);
    console.log("Manual response length:", response.length);
    console.log(
      "Manual response preview:",
      response.substring(0, 1000) + "...",
    );

    // Parse the response
    let responseText = response;
    if (typeof response === "object" && response.message) {
      responseText = response.message;
      console.log("Extracted message from manual object response");
    }

    console.log("Manual text response type:", typeof responseText);
    console.log(
      "Manual text response preview:",
      responseText.substring(0, 500),
    );

    // Remove markdown code blocks if present
    let jsonText = responseText.trim();
    console.log("Manual original trimmed text:", jsonText.substring(0, 200));

    jsonText = jsonText.replace(/```json\s*/g, "").replace(/```\s*/g, "");
    console.log("Manual after removing markdown:", jsonText.substring(0, 200));

    // Extract JSON object
    const jsonMatch = jsonText.match(/\{[\s\S]*\}/);
    console.log(
      "Manual JSON match result:",
      jsonMatch ? "Found JSON" : "No JSON found",
    );

    if (!jsonMatch) {
      console.error(
        "No JSON found in manual response. Full response:",
        responseText,
      );
      console.log("=== Manual JSON Parsing Failed ===");
      throw new Error("No valid JSON found in OpenAI response");
    }

    console.log(
      "Manual JSON match found:",
      jsonMatch[0].substring(0, 300) + "...",
    );
    console.log("Manual JSON length:", jsonMatch[0].length);

    const jobData = JSON.parse(jsonMatch[0]);
    console.log("Manual successfully parsed JSON:", jobData);
    console.log("Manual parsed job data keys:", Object.keys(jobData));
    console.log("=== Manual JSON Parsing Success ===");

    // Validate required fields
    if (!jobData.jobTitle) {
      throw new Error("Missing required jobTitle in extracted data");
    }

    return {
      ...jobData,
      jobUrl: jobUrl || "",
    };
  } catch (error) {
    console.error("Error extracting from manual text:", error);
    console.log("=== Using Fallback for Manual Text Extraction ===");
    console.log("Reason for fallback:", error.message);

    // Extract job text from prompt for fallback
    const jobTextMatch = prompt.match(/Job Description Text:\n([\s\S]+)/);
    const jobText = jobTextMatch ? jobTextMatch[1] : "";

    // Use fallback extraction
    const fallbackData = fallbackExtraction(jobText, jobUrl);
    console.log("Fallback extracted data for manual text:", fallbackData);
    console.log("=== Manual Text Fallback Complete ===");

    return fallbackData;
  }
};

export { extractJobData, extractJobDataFromText, extractFromPastedJob };
